import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useStore } from "react-redux";

/**
 * ActionSequenceRunner (plain React, no TypeScript)
 *
 * Supply `items` as an ordered array. Each item can be one of:
 *   - { label, action }                 // `action` is a plain action object OR a thunk function already ready to dispatch
 *   - { label, actionFactory }          // builds an action/thunk at click time: ({ getState }) => action | thunk
 *   - { label, run }                    // advanced: ({ dispatch, getState }) => Promise | any
 *
 * Completion semantics:
 * - Thunks and RTK async thunks are awaited (we await the Promise returned by dispatch).
 * - Plain actions resolve immediately after dispatch.
 * - Action creators (functions returning plain actions) are supported — we'll call them with no args and dispatch the result.
 * - `actionFactory` can return an action or a thunk; it receives { dispatch, getState }.
 * - `run` may return a Promise; if so, we await it.
 */

const rowStyle = { display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, alignItems: "center", padding: "8px 0", borderBottom: "1px solid #eee" };
const btnStyle = { padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", cursor: "pointer", background: "white" };
const execAllStyle = { ...btnStyle, background: "#f6f6ff" };
const statusDot = (s) => ({
  display: "inline-block",
  width: 10,
  height: 10,
  borderRadius: "50%",
  marginRight: 8,
  background: s === "idle" ? "#bbb" : s === "running" ? "#8888ff" : s === "ok" ? "#22c55e" : "#ef4444",
});

export default function ActionSequenceRunner({ items, disableAll }) {
  const dispatch = useDispatch();
  const store = useStore();
  const getState = store.getState;

  const [meta, setMeta] = useState(() => items.map(() => ({ status: "idle" })));
  const [isAllRunning, setIsAllRunning] = useState(false);

  // reset meta if items length changes
  useEffect(() => { setMeta(items.map(() => ({ status: "idle" }))); }, [items.length]);

  const isPlainObject = (v) => v && typeof v === "object" && (v.constructor === Object || Object.getPrototypeOf(v) === null);

  const ensurePromise = (val) => {
    if (val && typeof val.then === "function") return val; // Promise
    return Promise.resolve(val);
  };

  const dispatchAndWait = (what) => {
    const out = dispatch(what); // thunk -> Promise, action -> object
    return out && typeof out.then === "function" ? out : Promise.resolve(out);
  };

  const makeExecutor = (step) => async () => {
    // Priority: actionFactory -> action -> run
    if (typeof step.actionFactory === "function") {
      const built = step.actionFactory({ dispatch, getState });
      if (typeof built === "function") return dispatchAndWait(built); // thunk
      return dispatchAndWait(built); // plain action
    }

    if (typeof step.action !== "undefined") {
      const a = step.action;
      if (typeof a === "function") {
        // Could be a thunk OR a zero-arg action creator. Try calling; if it returns a plain action or a thunk, use that.
        try {
          const maybe = a();
          if (isPlainObject(maybe) || typeof maybe === "function") {
            return dispatchAndWait(typeof maybe === "function" ? maybe : maybe);
          }
        } catch (_) { /* ignore and fall back to dispatching as thunk */ }
        return dispatchAndWait(a); // treat as thunk
      }
      return dispatchAndWait(a); // plain action object
    }

    if (typeof step.run === "function") {
      return ensurePromise(step.run({ dispatch, getState }));
    }

    throw new Error("Step is missing action/actionFactory/run");
  };

  const runStep = useCallback(async (index) => {
    setMeta((m) => m.map((mm, i) => (i === index ? { ...mm, status: "running", error: null, startedAt: Date.now(), finishedAt: undefined } : mm)));
    try {
      const exec = makeExecutor(items[index]);
      await exec();
      setMeta((m) => m.map((mm, i) => (i === index ? { ...mm, status: "ok", finishedAt: Date.now() } : mm)));
      return true;
    } catch (e) {
      setMeta((m) => m.map((mm, i) => (i === index ? { ...mm, status: "err", error: String(e && e.message ? e.message : e), finishedAt: Date.now() } : mm)));
      return false;
    }
  }, [items]);

  const runAll = useCallback(async () => {
    setIsAllRunning(true);
    for (let i = 0; i < items.length; i++) {
      const ok = await runStep(i);
      if (!ok) break; // stop chain on first error
    }
    setIsAllRunning(false);
  }, [items, runStep]);

  const resetAll = useCallback(() => {
    setMeta(items.map(() => ({ status: "idle" })));
  }, [items]);

  const allOk = useMemo(() => meta.length && meta.every((m) => m.status === "ok"), [meta]);

  return (
    <div class="outline" style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, background: "#fff", width:300,

      top:'100%',


      //bottom:0,

      }}>
      {/* Example usage:
        <ActionSequenceRunner items={[
          // Plain action object
          { label: "Login (user 1)", action: actions.login({ id: "u1" }) },
          // Build at runtime using factory
          { label: "Create a directory", actionFactory: () => actions.createDirectory({ name: "experiments" }) },
          // Zero-arg action creator (we'll call it for you)
          { label: "Save", action: actions.save },
          // Thunk (function) — we dispatch and await
          { label: "Logout", action: () => (dispatch) => { dispatch(actions.logout()); } },
          { label: "Login (user 2)", action: actions.login({ id: "u2" }) },
          { label: "Create something", actionFactory: () => actions.createSomething({ foo: 1 }) },
          { label: "Logout", action: actions.logout },
          { label: "Log back in user 1", action: actions.login({ id: "u1" }) },
        ]} />
      */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginBottom: 8 }}>
        {!disableAll && (
          <button onClick={runAll} disabled={isAllRunning} style={execAllStyle}>
            {isAllRunning ? "Running…" : "Execute All"}
          </button>
        )}
        <button onClick={resetAll} style={btnStyle}>Reset</button>
      </div>

      <div>
        {items.map((step, i) => (
          <div key={step.id || i} style={rowStyle}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={statusDot(meta[i]?.status || "idle")} />
              <span style={{ fontWeight: 500 }}>{step.label}</span>
            </div>
            <small style={{ justifySelf: "end", opacity: 0.7 }}>
              {meta[i]?.startedAt && !meta[i]?.finishedAt && "running"}
              {meta[i]?.finishedAt && meta[i]?.startedAt && `${Math.max(1, meta[i].finishedAt - meta[i].startedAt)} ms`}
            </small>
            <div>
              <button
                onClick={() => runStep(i)}
                disabled={meta[i]?.status === "running" || isAllRunning}
                style={btnStyle}
              >
                Execute
              </button>
            </div>
          </div>
        ))}
      </div>

      {meta.some((m) => m.error) && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed #eee" }}>
          {meta.map((m, i) => m.error ? (
            <div key={`err-${i}`} style={{ color: "#b91c1c", fontSize: 12 }}>
              Step {i + 1}: {m.error}
            </div>
          ) : null)}
        </div>
      )}

      {allOk && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#16a34a" }}>All steps executed successfully.</div>
      )}
    </div>
  );
}
